import { useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import { DateTime } from 'luxon';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName, IconPrefix } from '@fortawesome/free-solid-svg-icons';

import { LiveGraph } from './LiveGraph';
import { ValueAction } from './ValueAction';
import { ToggleAction } from './ToggleAction';
import { SelectAction } from './SelectAction';
import { IUser } from '../../../types/User/User';
import { Card } from '../../../components/base/Card';
import useApiHelper from '../../../hooks/useApiHelper';
import { addTimeout } from '../../../utils/addTimeout';
import { useUserContext } from '../../../contexts/UserContext';
import { IDataPoint } from '../../../types/DataPoint/DataPoint';
import { IAppliance } from '../../../types/Appliance/Appliance';
import { PendingActionState } from '../../../types/Action/Action';
import { useLiveReading } from '../../../contexts/LiveReadingContext';
import useOrganisation from '../../../data/organisation/useOrganisation';
import { IActionProfile, InputType } from '../../../types/Action/ActionProfile';
import useOrganisationUsers from '../../../data/organisationUsers/useOrganisationUsers';
import {
  PendingAction,
  usePendingActions,
} from '../../../contexts/PendingActionContext';
import {
  IProcessedReading,
  ProcessedReading,
  useReadingsProcessor,
} from '../../../hooks/useReadingProcessor';
import usePlanetDevice from '../../../data/planetDevice/usePlanetDevice';
import { useNotification } from '../../../hooks/useNotifications';
import { SubscriptionTier } from '../../../types/Billing/Subscription';
import { Button } from '../../base/Button';
import { useNavigate } from 'react-router';

interface ReadingCardProps {
  appliance: IAppliance;
  dataPoint: IDataPoint;
  timestamp?: string;
}

export function ReadingCard({
  appliance,
  dataPoint,
  timestamp,
  ...rest
}: ReadingCardProps) {
  const addNotification = useNotification();
  const navigate = useNavigate();
  const { post } = useApiHelper();
  const { hasPendingAction, addPendingAction, removePendingAction } =
    usePendingActions();
  const { planetDevice } = usePlanetDevice(appliance.deviceId ?? '');
  const { processorReady, readingProcessor } = useReadingsProcessor();
  const { latest } = useLiveReading();
  const { organisation } = useOrganisation();
  const { users } = useOrganisationUsers();
  const { userId } = useUserContext();
  const [organisationUser, setOrganisationUser] = useState<IUser>();
  const [processedReading, setProcessedReading] = useState<ProcessedReading>();
  const [pendingAction, setPendingAction] =
    useState<PendingActionState>('idle');
  const actionTimedOutTimeOut = useRef<NodeJS.Timeout>();

  const handleApplyAction = (val: string, actionId: string) => {
    const valueParts = val.split('.');
    // Check to see if we have more than one item after split (we have a . in the string)
    // Then check to see if the string after the . is not 0
    const isFloat =
      valueParts.length > 1 ? (valueParts[1] !== '0' ? true : false) : false;
    const value = isFloat ? parseFloat(val) : parseInt(val);

    setPendingAction('pending');

    post(`/v1/action/${appliance.id}`, {
      value,
      action: actionId,
    })
      .then(() => {
        addPendingAction({
          dataPointId: dataPoint.id,
          expectedValue: value,
          timeout: addTimeout(
            dataPoint.actions[0].responseTimeout === 0
              ? 60
              : dataPoint.actions[0].responseTimeout!
          ),
        });
      })
      .catch((e) => {
        if (e.response?.status === 429) {
          setPendingAction('timedOut');

          addNotification('error', e.response.data.message);

          setTimeout(() => {
            setPendingAction('idle');
          }, 2000);

          console.warn('Rate limit exceeded. Please try again later.');
        } else {
          setPendingAction('timedOut');

          setTimeout(() => {
            setPendingAction('idle');
          }, 2000);

          console.error('An error occurred:', e);
        }
      });
  };

  const getAction = (action: IActionProfile) => {
    let conencted =
      planetDevice.data && planetDevice.data.length > 0
        ? planetDevice.data![0].gateway?.device.cloud.connected
        : false;

    const pr = processedReading as IProcessedReading;

    const props = {
      onApply: handleApplyAction,
      actionId: action.id,
      pendingAction: pendingAction,
      currentValue: pr.hasValue ? pr.reading.transformedValue! : 0,
      disabled:
        !pr.hasValue ||
        !conencted ||
        organisation.data?.readRole === organisationUser?.roleId,
      paidTier:
        planetDevice.data?.[0]?.billing.tier === SubscriptionTier.Standard,
      appliance,
    };

    switch (action.inputType) {
      case InputType.None:
        return <></>;
      case InputType.Select:
        return <SelectAction {...props} options={pr.dataPoint.options!} />;
      case InputType.Toggle:
        let toggleOpts = pr.dataPoint.options
          .filter((opt) => !opt.readOnly)
          .sort((a, b) => a.label.localeCompare(b.label))
          .reverse();

        if (toggleOpts?.length! < 2) {
          return <></>;
        } else {
          return (
            <ToggleAction
              {...props}
              leftOption={toggleOpts![0]}
              rightOption={toggleOpts![1]}
            />
          );
        }
      case InputType.Value:
      default:
        return (
          <ValueAction
            {...props}
            step={action.step}
            min={pr.dataPoint.profiles[0].minimumValue}
            max={pr.dataPoint.profiles[0].maximumValue}
            precision={pr.dataPoint.profiles[0].precision}
            unit={pr.dataPoint.unit}
          />
        );
    }
  };

  useEffect(() => {
    if (!organisationUser && !organisation.isLoading && !users.isLoading) {
      setOrganisationUser(users.data?.find((u) => u.id === userId));
    }
  }, [
    organisation.isLoading,
    users.isLoading,
    users.data,
    userId,
    organisationUser,
  ]);

  useEffect(() => {
    //Find reading for DataPoint
    const reading = latest?.readingData.find(
      (r) => r.dataPointId === dataPoint.id
    );

    if (reading) {
      // Throw reading & dataPoint for this card into processor
      let updatedReading = readingProcessor(reading!, dataPoint);

      if (updatedReading.hasValue) {
        let pendingAction = hasPendingAction(dataPoint.id);

        // If a pending action is stored
        if (pendingAction) {
          if (pendingAction === 'timedOut') {
            setPendingAction('timedOut');
            actionTimedOutTimeOut.current = setTimeout(
              () => setPendingAction('idle'),
              5000
            );
          } else {
            let pa = pendingAction as PendingAction;

            if (updatedReading?.reading.transformedValue === pa.expectedValue) {
              // Update worked, clear flag
              setPendingAction('idle');
              removePendingAction(dataPoint.id);
            }
          }
        }
      } else {
        console.warn(`No value for datapoint ${dataPoint.name} `);
      }

      setProcessedReading(updatedReading);
    } else {
      //Fake a reading
      let updatedReading = readingProcessor(null, dataPoint);
      setProcessedReading(updatedReading);
    }

    return () => {
      if (actionTimedOutTimeOut.current) {
        clearTimeout(actionTimedOutTimeOut.current);
      }
    };
  }, [latest, readingProcessor]);

  if (!processorReady || organisation.isLoading || users.isLoading) {
    return <></>;
  } else {
    if (!processedReading) {
      return <></>;
    } else {
      const displayIcon = processedReading.dataPoint.icon
        ? (processedReading.dataPoint.icon.split(',') as [IconPrefix, IconName])
        : (['fas', 'question'] as [IconPrefix, IconName]);

      const getHeight = () => {
        if (
          processedReading.dataPoint.actions.length > 0 &&
          processedReading.dataPoint.graphEnable
        ) {
          return 352;
        } else if (processedReading.dataPoint.actions.length > 0) {
          return 176;
        } else if (processedReading.dataPoint.graphEnable) {
          return 264;
        } else {
          return 88;
        }
      };

      if (
        processedReading.dataPoint.id === '0382fb19-e4cb-4853-9b71-08db89c1af75'
      ) {
        console.log(processedReading);
      }

      return (
        <Card {...rest} className={`h-[${getHeight()}] w-full`}>
          <div className="p-1 grid grid-cols-6 gap-2">
            <div className="flex items-center justify-center">
              <FontAwesomeIcon
                icon={displayIcon}
                className="h-auto max-h-20 aspect-square text-gray-500 dark:text-gray-300"
              />
            </div>
            <div className="col-span-5">
              <h4 className="text-2xl text-right">
                {processedReading.dataPoint.name}
              </h4>
              <h2
                className={clsx(
                  'text-5xl text-right',
                  !processedReading.hasValue &&
                    'text-gray-400 dark:text-gray-600'
                )}
              >
                {processedReading.displayString}
              </h2>
            </div>
          </div>
          {processedReading.dataPoint.id ===
            '0382fb19-e4cb-4853-9b71-08db89c1af75' &&
          processedReading.hasValue &&
          processedReading.reading.transformedValue === 1 &&
          planetDevice.data?.[0]?.billing.tier !== SubscriptionTier.Free ? (
            <div className="mt-4">
              <Button
                fullWidth
                size="lg"
                onClick={() =>
                  navigate('/faults', { state: { applianceId: appliance.id } })
                }
              >
                Go to Faults
              </Button>
            </div>
          ) : (
            Boolean(
              processedReading.dataPoint.graphEnable &&
              appliance !== undefined &&
              planetDevice.data?.[0]?.billing.tier !== SubscriptionTier.Free
            ) && (
              <div className="mt-4">
                <LiveGraph
                  hardwareId={appliance.id}
                  dataPoint={processedReading.dataPoint}
                  endDate={latest ? latest.timestamp : DateTime.now().toISO()!}
                />
              </div>
            )
          )}
          {processedReading.dataPoint.actions.length > 0 && (
            <div className="mt-4">
              {getAction(processedReading.dataPoint.actions[0])}
            </div>
          )}
        </Card>
      );
    }
  }
}
